
import React, { useState } from 'react';
import { User, UserRole } from '../types';
import {
  User as UserIcon, Mail, Shield, Building2, Bell,
  Lock, CreditCard, LogOut, CheckCircle2, Star, Clock, ChevronRight, Users, ChevronLeft,
  Calendar as CalendarIcon, Link as LinkIcon, Globe, X, Loader2, Save, Key, AlertCircle, Settings, Sparkles, Briefcase, Camera, BrainCircuit
} from 'lucide-react';
import TeamManagement from './TeamManagement';
import AIRecruiterSettings from './AIRecruiterSettings';
import CompanySettings from './CompanySettings';
import { connectGoogleAccount } from '../services/calendarService';
import { supabase, extractErrorMessage, supabase_update_client } from '../services/supabaseService';

const Profile: React.FC<{ user: User, onLogout: () => void }> = ({ user, onLogout }) => {
  const isClientPortal = window.location.hostname.toLowerCase().includes('clientportal') || new URLSearchParams(window.location.search).get('clientportal') === 'true';
  const [showTeam, setShowTeam] = useState(false);
  const [showAiSettings, setShowAiSettings] = useState(false);
  const [showCompany, setShowCompany] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showContractModal, setShowContractModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [editName, setEditName] = useState(user.name || '');
  const [editPhone, setEditPhone] = useState(user.phone || '');
  const [editAvatar, setEditAvatar] = useState(user.avatar || '');
  const [isConnecting, setIsConnecting] = useState(false);
  const [isGCalConnected, setIsGCalConnected] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);



  const [passData, setPassData] = useState({
    newPassword: '',
    confirmPassword: ''
  });

  const [contractOptionsStr, setContractOptionsStr] = useState('Vast, Tijdelijk, ZZP, Interim, Gedetacheerd');
  const [isLoadingContracts, setIsLoadingContracts] = useState(false);

  // Fetch client settings when opening contract modal
  React.useEffect(() => {
    if (showContractModal && user.clientId) {
      setIsLoadingContracts(true);
      supabase.from('clients').select('contract_options').eq('id', user.clientId).single()
        .then(({ data }) => {
          if (data && data.contract_options) {
            setContractOptionsStr(data.contract_options.join(', '));
          }
        })
        .finally(() => setIsLoadingContracts(false));
    }
  }, [showContractModal, user.clientId]);

  const handleSaveContractOptions = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user.clientId) return;
    setIsProcessing(true);
    setError(null);
    try {
      const optionsArray = contractOptionsStr.split(',').map(s => s.trim()).filter(s => s.length > 0);
      await supabase_update_client(user.clientId, { contract_options: optionsArray });
      alert("Contract opties succesvol opgeslagen!");
      setShowContractModal(false);
    } catch (err: any) {
      setError(extractErrorMessage(err));
    } finally {
      setIsProcessing(false);
    }
  };


  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsProcessing(true);
    setError(null);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}_${Math.random()}.${fileExt}`;
      const filePath = `public/avatars/${fileName}`;
      const { error: uploadError } = await supabase.storage.from('public').upload(filePath, file, { upsert: true });
      if (uploadError) throw uploadError;
      const { data } = supabase.storage.from('public').getPublicUrl(filePath);
      setEditAvatar(data.publicUrl);
    } catch (err: any) {
      setError(extractErrorMessage(err));
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsProcessing(true);
    setError(null);
    try {
      const { error: profileError } = await supabase.from('profiles').update({ name: editName, phone: editPhone, avatar_url: editAvatar }).eq('id', user.id);
      if (profileError) throw profileError;
      alert("Profiel succesvol bijgewerkt. Herlaad de pagina om wijzigingen te zien.");
      setShowProfileModal(false);
    } catch (err: any) {
      setError(extractErrorMessage(err));
    } finally {
      setIsProcessing(false);
    }
  };

  const handleConnectGCal = async () => {
    setIsConnecting(true);
    try {
      await connectGoogleAccount();
      setIsGCalConnected(true);
    } finally {
      setIsConnecting(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (passData.newPassword !== passData.confirmPassword) {
      setError("Wachtwoorden komen niet overeen.");
      return;
    }
    if (passData.newPassword.length < 6) {
      setError("Wachtwoord moet minimaal 6 tekens bevatten.");
      return;
    }

    setIsProcessing(true);
    setError(null);
    try {
      const { error: updateError } = await supabase.auth.updateUser({ password: passData.newPassword });
      if (updateError) throw updateError;
      alert("Wachtwoord succesvol gewijzigd!");
      setShowPasswordModal(false);
      setPassData({ newPassword: '', confirmPassword: '' });
    } catch (err: any) {
      setError(extractErrorMessage(err));
    } finally {
      setIsProcessing(false);
    }
  };

  if (showTeam) {
    return (
      <div className="space-y-6 p-6 lg:p-10 animate-fadeIn">
        <button onClick={() => setShowTeam(false)} className="text-xs font-black text-slate-500 hover:text-slate-900 flex items-center gap-2 uppercase tracking-widest transition-colors">
          <ChevronLeft size={16} /> Back to Profile
        </button>
        <TeamManagement />
      </div>
    );
  }

  if (showAiSettings) {
    return (
      <div className="space-y-6 p-6 lg:p-10 animate-fadeIn">
        <button onClick={() => setShowAiSettings(false)} className="text-xs font-black text-slate-500 hover:text-slate-900 flex items-center gap-2 uppercase tracking-widest transition-colors">
          <ChevronLeft size={16} /> Back to Profile
        </button>
        <AIRecruiterSettings user={user} />
      </div>
    );
  }

  if (showCompany) {
    return (
      <div className="space-y-6 p-6 lg:p-10 animate-fadeIn">
        <CompanySettings user={user} onBack={() => setShowCompany(false)} />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6 lg:p-10 max-w-5xl mx-auto pb-20 animate-fadeIn">
      {/* HEADER SECTION */}
      <div className="flex items-start justify-between bg-white p-8 rounded-xl border border-slate-200 shadow-sm">
        <div className="flex items-center gap-6">
          <div className="w-16 h-16 bg-[#Ffe8e8] rounded-full flex items-center justify-center overflow-hidden border border-red-100 shadow-sm shrink-0 group relative cursor-pointer" onClick={() => setShowProfileModal(true)} title="Foto aanpassen">
             {user.avatar ? (
                 <img src={user.avatar} className="w-full h-full object-cover" alt={user.name} />
             ) : (
                 <UserIcon className="text-[#e51b23]" size={32} />
             )}
             <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white">
                 <Camera size={20} />
             </div>
          </div>
          <div>
            <h1 className="text-xl font-black text-slate-900 tracking-tight leading-tight flex items-center gap-2">
              {user.name}
            </h1>
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 mt-2">
              <span className="flex items-center gap-1.5 text-xs font-bold text-slate-500">
                <Shield size={14} className="text-slate-400" /> {user.role}
              </span>
              <span className="hidden sm:inline text-slate-300">•</span>
              <span className="flex items-center gap-1.5 text-xs font-bold text-slate-500">
                <Mail size={14} className="text-slate-400" /> {user.email}
              </span>
              <span className="hidden sm:inline text-slate-300">•</span>
              <span className="flex items-center gap-1.5 text-xs font-bold text-slate-500">
                <Globe size={14} className="text-slate-400" /> {user.phone || 'Geen telefoon'}
              </span>
              {user.clientName && (
                <>
                  <span className="hidden sm:inline text-slate-300">•</span>
                  <span className="flex items-center gap-1.5 text-xs font-bold text-slate-500">
                    <Building2 size={14} className="text-slate-400" /> {user.clientName}
                  </span>
                </>
              )}
            </div>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <button onClick={() => setShowProfileModal(true)} className="bg-white border text-xs font-bold border-slate-200 text-slate-700 px-4 py-2 rounded-lg hover:bg-slate-50 transition-colors shadow-sm">
            Edit Profile
          </button>
          <button onClick={onLogout} className="bg-white border text-xs font-bold border-red-200 text-red-600 px-4 py-2 rounded-lg hover:bg-red-50 transition-colors shadow-sm">
            Sign Out
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* SETTINGS MENU */}
        <div className="lg:col-span-2 space-y-4">
          <h2 className="text-sm font-black text-slate-900 uppercase tracking-widest pl-1">Configuration</h2>
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm divide-y divide-slate-100 overflow-hidden">
            
            {user.role === UserRole.CLIENT && !isClientPortal && (
              <button onClick={() => setShowTeam(true)} className="w-full px-6 py-5 flex items-center justify-between hover:bg-slate-50 transition-colors text-left group border-b border-slate-100">
                <div className="flex items-center gap-4">
                  <Users size={20} className="text-slate-400 group-hover:text-slate-700 transition-colors" />
                  <div>
                    <h3 className="font-bold text-slate-900 text-sm">Team Management</h3>
                    <p className="text-xs text-slate-500 mt-0.5">Manage users and invite colleagues</p>
                  </div>
                </div>
                <ChevronRight size={18} className="text-slate-300 group-hover:text-slate-500" />
              </button>
            )}

            {user.clientId && !isClientPortal && (user.role === UserRole.CLIENT || user.role === UserRole.CLIENT_RECRUITER || user.role === UserRole.ADMIN) && (
              <button onClick={() => setShowCompany(true)} className="w-full px-6 py-5 flex items-center justify-between hover:bg-slate-50 transition-colors text-left group border-b border-slate-100">
                <div className="flex items-center gap-4">
                  <Building2 size={20} className="text-slate-400 group-hover:text-red-500 transition-colors" />
                  <div>
                    <h3 className="font-bold text-slate-900 text-sm">Bedrijfsinstellingen</h3>
                    <p className="text-xs text-slate-500 mt-0.5">Manage company details, logo, and billing</p>
                  </div>
                </div>
                <ChevronRight size={18} className="text-slate-300 group-hover:text-slate-500" />
              </button>
            )}

            {!isClientPortal && (
              <button onClick={() => setShowAiSettings(true)} className="w-full px-6 py-5 flex items-center justify-between hover:bg-slate-50 transition-colors text-left group">
                <div className="flex items-center gap-4">
                  <Sparkles size={20} className="text-slate-400 group-hover:text-amber-500 transition-colors" />
                  <div>
                    <h3 className="font-bold text-slate-900 text-sm">AI Recruiter Settings</h3>
                    <p className="text-xs text-slate-500 mt-0.5">Configure your automated chat agents</p>
                  </div>
                </div>
                <ChevronRight size={18} className="text-slate-300 group-hover:text-slate-500" />
              </button>
            )}

            {user.role === UserRole.CLIENT && !isClientPortal && (
              <button onClick={() => setShowContractModal(true)} className="w-full px-6 py-5 flex items-center justify-between hover:bg-slate-50 transition-colors text-left group">
                <div className="flex items-center gap-4">
                  <Briefcase size={20} className="text-slate-400 group-hover:text-red-0 transition-colors" />
                  <div>
                    <h3 className="font-bold text-slate-900 text-sm">Contract Options</h3>
                    <p className="text-xs text-slate-500 mt-0.5">Define available contract types for vacancies</p>
                  </div>
                </div>
                <ChevronRight size={18} className="text-slate-300 group-hover:text-slate-500" />
              </button>
            )}

            <button onClick={() => setShowPasswordModal(true)} className="w-full px-6 py-5 flex items-center justify-between hover:bg-slate-50 transition-colors text-left group">
              <div className="flex items-center gap-4">
                <Lock size={20} className="text-slate-400 group-hover:text-red-500 transition-colors" />
                <div>
                  <h3 className="font-bold text-slate-900 text-sm">Security & Password</h3>
                  <p className="text-xs text-slate-500 mt-0.5">Update access credentials</p>
                </div>
              </div>
              <ChevronRight size={18} className="text-slate-300 group-hover:text-slate-500" />
            </button>

            {!isClientPortal && (
            <button className="w-full px-6 py-5 flex items-center justify-between hover:bg-slate-50 transition-colors text-left group">
              <div className="flex items-center gap-4">
                <Bell size={20} className="text-slate-400 group-hover:text-indigo-500 transition-colors" />
                <div>
                  <h3 className="font-bold text-slate-900 text-sm">Notifications</h3>
                  <p className="text-xs text-slate-500 mt-0.5">Email and app alert preferences</p>
                </div>
              </div>
              <ChevronRight size={18} className="text-slate-300 group-hover:text-slate-500" />
            </button>
            )}
          </div>
        </div>

        {/* SIDE COLUMN */}
        {!isClientPortal && (
        <div className="space-y-6 lg:mt-9">
          <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
             <div className="flex items-center gap-3 mb-4">
                <CalendarIcon size={18} className="text-slate-400" />
                <h3 className="text-sm font-bold text-slate-900">Calendar Integration</h3>
             </div>
             <p className="text-xs text-slate-500 mb-6">Sync interviews and tasks automatically to your Google Calendar.</p>

             {isGCalConnected ? (
               <div className="space-y-3">
                 <div className="flex items-center gap-2 p-3 bg-emerald-50 text-emerald-700 text-xs font-bold rounded-lg border border-emerald-100">
                    <CheckCircle2 size={16} /> Connected to Google
                 </div>
                 <button onClick={() => setIsGCalConnected(false)} className="w-full py-2 bg-white text-slate-600 border border-slate-200 rounded-lg text-xs font-bold hover:bg-slate-50 transition-colors">
                    Disconnect
                 </button>
               </div>
             ) : (
               <button onClick={handleConnectGCal} disabled={isConnecting} className="w-full py-2.5 bg-red- text-white rounded-lg text-xs font-bold flex items-center justify-center gap-2 hover:bg-red- transition-colors disabled:opacity-50 shadow-sm">
                 <LinkIcon size={14} /> Connect Account
               </button>
             )}
          </div>

          <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
             <div className="flex items-center gap-3 mb-4">
                <Star size={18} className="text-slate-400" />
                <h3 className="text-sm font-bold text-slate-900">Performance Summary</h3>
             </div>
             <div className="space-y-3">
                <div className="flex justify-between items-center pb-2 border-b border-slate-100">
                   <span className="text-xs text-slate-500 font-medium">Active Vacancies</span>
                   <span className="text-sm font-black text-slate-900">12</span>
                </div>
                <div className="flex justify-between items-center pb-2 border-b border-slate-100">
                   <span className="text-xs text-slate-500 font-medium">Total Hires (YTD)</span>
                   <span className="text-sm font-black text-slate-900">42</span>
                </div>
                <div className="flex justify-between items-center">
                   <span className="text-xs text-slate-500 font-medium">Avg Time-to-Fill</span>
                   <span className="text-sm font-black text-slate-900">22 days</span>
                </div>
             </div>
          </div>

          <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm relative overflow-hidden">
             <div className="absolute top-0 right-0 w-24 h-24 bg-red-50 rounded-full blur-2xl -mr-8 -mt-8 pointer-events-none"></div>
             <div className="relative">
                 <div className="flex items-center justify-between mb-4">
                     <div className="flex items-center gap-3">
                        <BrainCircuit size={18} className="text-[#e51b23]" />
                        <h3 className="text-sm font-bold text-slate-900">API Token Verbruik</h3>
                     </div>
                     <span className="text-[10px] font-black uppercase tracking-widest text-[#e51b23] bg-red-50 border border-red-100 px-2 py-1 rounded-md">Live</span>
                 </div>
                 <p className="text-xs text-slate-500 mb-6">Totaalverbruik en geschatte datakosten van Gemini 2.0 Flash AI over het hele account.</p>

                 <div className="space-y-3">
                    <div className="flex justify-between items-center pb-2 border-b border-slate-100">
                       <span className="text-xs text-slate-500 font-medium">Input Tokens (Lezen)</span>
                       <span className="text-sm font-black text-slate-900">{(user.tokensInput || 0).toLocaleString('nl-NL')}</span>
                    </div>
                    <div className="flex justify-between items-center pb-2 border-b border-slate-100">
                       <span className="text-xs text-slate-500 font-medium">Output Tokens (Schrijven)</span>
                       <span className="text-sm font-black text-slate-900">{(user.tokensOutput || 0).toLocaleString('nl-NL')}</span>
                    </div>
                    <div className="flex justify-between items-center pt-2">
                       <span className="text-xs text-slate-700 font-bold">Totale Kosten (USD)</span>
                       <span className="text-sm font-black text-emerald-600">
                         ${((
                            ((user.tokensInput || 0) / 1000000) * 0.075
                          ) + (
                            ((user.tokensOutput || 0) / 1000000) * 0.30
                          )).toFixed(4)}
                       </span>
                    </div>
                 </div>
             </div>
          </div>
        </div>
        )}
      </div>

      {/* PASSWORD CHANGE MODAL */}
      {showPasswordModal && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-6 bg-slate-900/70 backdrop-blur-md animate-fadeIn">
          <div className="bg-white w-full max-w-md rounded-[2rem] shadow-2xl p-8 animate-scaleIn relative">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-xl font-black text-slate-900 uppercase tracking-tight">Change Password</h2>
              <button onClick={() => setShowPasswordModal(false)} className="p-2 text-slate-400 hover:text-red-600 transition-colors bg-slate-50 hover:bg-red-50 rounded-full"><X size={20} /></button>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded-xl text-red-600 text-xs font-bold flex items-center gap-3">
                <AlertCircle size={18} /> {error}
              </div>
            )}

            <form onSubmit={handleChangePassword} className="space-y-5">
              <div className="space-y-1">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">New Password</label>
                <input
                  type="password"
                  value={passData.newPassword}
                  onChange={e => setPassData({ ...passData, newPassword: e.target.value })}
                  className="w-full p-4 bg-slate-50 border-0 rounded-xl text-sm font-bold outline-none focus:ring-2 focus:ring-slate-200 transition-all"
                  placeholder="Min 6 chars"
                  required
                />
              </div>
              <div className="space-y-1">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Confirm Password</label>
                <input
                  type="password"
                  value={passData.confirmPassword}
                  onChange={e => setPassData({ ...passData, confirmPassword: e.target.value })}
                  className="w-full p-4 bg-slate-50 border-0 rounded-xl text-sm font-bold outline-none focus:ring-2 focus:ring-slate-200 transition-all"
                  placeholder="Repeat new password"
                  required
                />
              </div>
              <button
                type="submit"
                disabled={isProcessing}
                className="w-full bg-[#e51b23] text-white p-4 rounded-xl font-black text-xs uppercase tracking-widest shadow-lg hover:bg-black transition-all flex items-center justify-center gap-2 active:scale-[0.98] disabled:opacity-50 mt-4"
              >
                {isProcessing ? <Loader2 className="animate-spin" size={16} /> : (
                  <>
                    <Save size={16} /> Update Password
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* CONTRACT OPTIONS MODAL */}
      {showContractModal && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-6 bg-slate-900/70 backdrop-blur-md animate-fadeIn">
          <div className="bg-white w-full max-w-md rounded-[2rem] shadow-2xl p-8 animate-scaleIn relative">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-xl font-black text-slate-900 uppercase tracking-tight">Contract Opties</h2>
              <button onClick={() => setShowContractModal(false)} className="p-2 text-slate-400 hover:text-red-600 transition-colors bg-slate-50 hover:bg-red-50 rounded-full"><X size={20} /></button>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded-xl text-red-600 text-xs font-bold flex items-center gap-3">
                <AlertCircle size={18} /> {error}
              </div>
            )}

            <form onSubmit={handleSaveContractOptions} className="space-y-5">
              <div className="space-y-1">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Mogelijke typen (komma gescheiden)</label>
                {isLoadingContracts ? (
                  <div className="p-4 text-center text-slate-400 text-xs font-bold"><Loader2 className="animate-spin inline mr-2" size={14} /> Laden...</div>
                ) : (
                  <textarea
                    value={contractOptionsStr}
                    onChange={e => setContractOptionsStr(e.target.value)}
                    className="w-full p-4 bg-slate-50 border-0 rounded-xl text-sm font-bold outline-none focus:ring-2 focus:ring-slate-200 transition-all resize-none h-32"
                    placeholder="Vast, ZZP, Interim..."
                    required
                  />
                )}
                <p className="text-[10px] text-slate-400 font-medium px-2 mt-2">Deze opties worden als dropdown getoond in de vacature intake.</p>
              </div>

              <button
                type="submit"
                disabled={isProcessing || isLoadingContracts}
                className="w-full bg-[#e51b23] text-white p-4 rounded-xl font-black text-xs uppercase tracking-widest shadow-lg hover:bg-black transition-all flex items-center justify-center gap-2 active:scale-[0.98] disabled:opacity-50 mt-4"
              >
                {isProcessing ? <Loader2 className="animate-spin" size={16} /> : (
                  <>
                    <Save size={16} /> Opslaan
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      )}



      {/* EDIT PROFILE MODAL */}
      {showProfileModal && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-6 bg-slate-900/70 backdrop-blur-md animate-fadeIn">
          <div className="bg-white w-full max-w-md rounded-[2rem] shadow-2xl p-8 animate-scaleIn relative">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-xl font-black text-slate-900 uppercase tracking-tight">Bewerk Profiel</h2>
              <button onClick={() => setShowProfileModal(false)} className="p-2 text-slate-400 hover:text-red-600 transition-colors bg-slate-50 hover:bg-red-50 rounded-full"><X size={20} /></button>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded-xl text-red-600 text-xs font-bold flex items-center gap-3">
                <AlertCircle size={18} /> {error}
              </div>
            )}

            <form onSubmit={handleSaveProfile} className="space-y-5">
              <div className="flex items-center gap-6 mb-6">
                 <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center overflow-hidden border border-slate-200 shrink-0 relative group">
                     {editAvatar ? (
                         <img src={editAvatar} className="w-full h-full object-cover" alt="Avatar" />
                     ) : (
                         <UserIcon className="text-slate-400" size={32} />
                     )}
                     <label className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center text-white cursor-pointer">
                         {isProcessing ? <Loader2 size={16} className="animate-spin mb-1"/> : <Camera size={16} className="mb-1" />}
                         <span className="text-[8px] font-black uppercase tracking-widest">Wijzigen</span>
                         <input type="file" accept="image/*" className="hidden" onChange={handleAvatarUpload} disabled={isProcessing} />
                     </label>
                 </div>
                 <div className="space-y-1 flex-1">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Profielfoto</label>
                    <p className="text-xs text-slate-500 font-medium leading-relaxed">Klik op de afbeelding om een nieuwe foto (.jpg of .png) te uploaden.</p>
                 </div>
              </div>

              <div className="space-y-1">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Naam</label>
                <input
                  type="text"
                  value={editName}
                  onChange={e => setEditName(e.target.value)}
                  className="w-full p-4 bg-slate-50 border-0 rounded-xl text-sm font-bold outline-none focus:ring-2 focus:ring-slate-200 transition-all"
                  placeholder="Volledige naam"
                  required
                />
              </div>
              <div className="space-y-1">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Telefoonnummer</label>
                <input
                  type="text"
                  value={editPhone}
                  onChange={e => setEditPhone(e.target.value)}
                  className="w-full p-4 bg-slate-50 border-0 rounded-xl text-sm font-bold outline-none focus:ring-2 focus:ring-slate-200 transition-all"
                  placeholder="+31612345678"
                />
                <p className="text-[10px] text-slate-400 font-medium px-2 mt-2">Wordt gebruikt als afzender bij AI telefoongesprekken.</p>
              </div>
              
              <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 mt-2">
                 <p className="text-[10px] font-bold uppercase tracking-widest text-[#e51b23] mb-1 flex items-center gap-1">
                     <Lock size={12} /> Contact Opnemen
                 </p>
                 <p className="text-xs text-slate-500 font-medium">Voor andere wijzigingen in je profiel (zoals je rol, bevoegdheden of emailadres), neem contact op met jouw werkruimte beheerder.</p>
              </div>

              <button
                type="submit"
                disabled={isProcessing}
                className="w-full bg-slate-900 text-white p-4 rounded-xl font-black text-xs uppercase tracking-widest shadow-lg hover:bg-black transition-all flex items-center justify-center gap-2 active:scale-[0.98] disabled:opacity-50 mt-4"
              >
                {isProcessing ? <Loader2 className="animate-spin" size={16} /> : (
                  <>
                    <Save size={16} /> Opslaan
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;
